import React, { useState, useRef } from 'react';
import { Camera } from 'lucide-react';
import { uploadApi } from '../lib/api';

interface UploadedImage {
  id: string;
  url: string;
  file: File;
  isUploading: boolean;
}

interface ChatImageUploadProps {
  clubId: string;
  images: UploadedImage[];
  onImagesChange: (images: UploadedImage[] | ((prevImages: UploadedImage[]) => UploadedImage[])) => void;
  maxImages?: number;
  disabled?: boolean;
  className?: string;
}

export function ChatImageUpload({ 
  clubId,
  images, 
  onImagesChange, 
  maxImages = 4,
  disabled = false,
  className = '' 
}: ChatImageUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File validation constants
  const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload JPG, PNG, WebP, or HEIC images only.';
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 6MB.';
    }
    
    return null;
  };

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const processFiles = async (fileList: FileList) => {
    const files = Array.from(fileList);
    const remainingSlots = maxImages - images.length;
    const filesToProcess = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      setError(`Can only upload ${remainingSlots} more image(s). Maximum ${maxImages} images per message.`);
    } else {
      setError(null);
    }

    // Validate all files first
    for (const file of filesToProcess) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Create preview images and start uploading
    const newImages: UploadedImage[] = filesToProcess.map(file => ({
      id: generateId(),
      url: URL.createObjectURL(file),
      file,
      isUploading: true
    }));

    // Add new images to state immediately for preview
    const updatedImages = [...images, ...newImages];
    onImagesChange(updatedImages);

    // Upload each image
    for (const image of newImages) {
      try {
        const response = await uploadApi.uploadClubChatImage(clubId, image.file);
        
        // Update the specific image with the uploaded URL
        // Use functional update to ensure we have latest state
        onImagesChange(currentImages => 
          currentImages.map((img: UploadedImage) => 
            img.id === image.id 
              ? { ...img, url: response.image_url, isUploading: false }
              : img
          )
        );
        
        // Clean up preview URL
        URL.revokeObjectURL(image.url);
      } catch (err) {
        // Remove failed upload and show error
        // Use functional update to ensure we have latest state
        onImagesChange(currentImages => 
          currentImages.filter((img: UploadedImage) => img.id !== image.id)
        );
        URL.revokeObjectURL(image.url);
        
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
        setError(errorMessage);
      }
    }
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;
    await processFiles(files);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await handleFileSelect(e.target.files);
    // Reset input value to allow re-selecting same file
    e.target.value = '';
  };

  const handleCameraClick = () => {
    if (disabled || images.length >= maxImages) return;
    fileInputRef.current?.click();
  };

  const canAddMore = images.length < maxImages && !disabled;

  return (
    <div className={className}>
      {/* Error Message - only show if there's an error */}
      {error && (
        <div className="absolute -top-12 left-0 right-0 bg-red-500/10 border border-red-500/40 rounded-lg p-2 text-xs text-red-200 mb-2">
          {error}
        </div>
      )}

      {/* Camera Button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        multiple
      />
      
      <button
        type="button"
        onClick={handleCameraClick}
        disabled={!canAddMore}
        className={`
          p-2 rounded-full transition-colors flex items-center justify-center
          ${canAddMore 
            ? 'bg-oro-surface text-accent border border-accent hover:bg-accent hover:text-black' 
            : 'bg-oro-border text-text-muted cursor-not-allowed'
          }
        `}
        title={canAddMore ? 'Add photos' : `Maximum ${maxImages} images`}
      >
        <Camera className="w-5 h-5" />
      </button>
    </div>
  );
}

export default ChatImageUpload;